// Sidekick voice + screen-effect dispatch for the Command Center.
// Voice uses a natural OpenAI TTS voice served by the backend, with a graceful
// fallback to the browser voice if TTS isn't available.
import axios from "axios";

const MUTE_KEY = "iw_cc_mute";

// Configured once by the Power Bar / Command Center so this lib can call the
// authenticated TTS endpoint.
let _cfg = { token: null, api: null, voice: "coral" };
let _audio = null;
let _lastUrl = null;

export function setVoiceConfig({ token, api, voice } = {}) {
  if (token !== undefined) _cfg.token = token;
  if (api !== undefined) _cfg.api = api;
  if (voice) _cfg.voice = voice;
}

export function isMuted() {
  try { return localStorage.getItem(MUTE_KEY) === "1"; } catch (_) { return false; }
}
export function setMuted(m) {
  try { localStorage.setItem(MUTE_KEY, m ? "1" : "0"); } catch (_) {}
  if (m) stopAll();
}

// Separate "effect whoosh" sound switch — lets the visual effects play in calm
// silence (no whoosh) while the spoken voice stays on. Default ON.
const FX_SOUND_KEY = "iw_cc_fx_sound";
export function isFxSoundOn() {
  try { return localStorage.getItem(FX_SOUND_KEY) !== "0"; } catch (_) { return true; }
}
export function setFxSound(on) {
  try { localStorage.setItem(FX_SOUND_KEY, on ? "1" : "0"); } catch (_) {}
}

function stopAll() {
  try { if (_audio) { _audio.pause(); _audio.currentTime = 0; } } catch (_) {}
  try { if (_lastUrl) { URL.revokeObjectURL(_lastUrl); _lastUrl = null; } } catch (_) {}
  try { if ("speechSynthesis" in window) window.speechSynthesis.cancel(); } catch (_) {}
}

// Robotic browser voice — only used if the natural TTS voice fails.
function browserSpeak(text) {
  try {
    if (!("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(String(text));
    u.rate = 1.0; u.pitch = 1.05; u.volume = 1;
    const voices = window.speechSynthesis.getVoices() || [];
    const pick = voices.find((v) => /en(-|_)?(US|GB)?/i.test(v.lang) && /google|samantha|natural|female/i.test(v.name))
      || voices.find((v) => /^en/i.test(v.lang));
    if (pick) u.voice = pick;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch (_) {}
}

// Talk out loud in a warm, natural sidekick voice.
export async function speak(text) {
  if (!text || isMuted()) return;
  stopAll();
  if (_cfg.token && _cfg.api) {
    try {
      const res = await axios.post(
        `${_cfg.api}/founder/tts`,
        { text: String(text), voice: _cfg.voice },
        { headers: { Authorization: `Bearer ${_cfg.token}` }, responseType: "blob" },
      );
      if (isMuted()) return;
      const url = URL.createObjectURL(res.data);
      _lastUrl = url;
      const a = new Audio(url);
      _audio = a;
      a.play().catch(() => browserSpeak(text));
      return;
    } catch (_) { /* fall back to the browser voice */ }
  }
  browserSpeak(text);
}

// Fire a full-screen effect anywhere in the app.
export function triggerEffect(effect) {
  if (!effect) return;
  try { window.dispatchEvent(new CustomEvent("founder:effect", { detail: { effect } })); } catch (_) {}
}

// Given an executed command, figure out which screen effect (if any) to play.
export function effectForResult(action, result) {
  if (!result) result = {};
  if (result.effect) return result.effect;
  if (action === "million_star_blast") return "money-rain";
  if (action === "god_mode" && result.active_godmode) return "rainbow-burst";
  if (result.celebration) return "confetti";
  return null;
}

// Fire several effects as an epic combo (staggered so each is visible).
export function triggerCombo(effects) {
  (effects || []).forEach((e, i) => setTimeout(() => triggerEffect(e), i * 420));
}

// Trigger the right visual for an executed command (single or combo).
export function fireResult(action, result) {
  if (result && Array.isArray(result.effects) && result.effects.length) {
    triggerCombo(result.effects);
    return;
  }
  const eff = effectForResult(action, result);
  if (eff) triggerEffect(eff);
}

// A short whoosh + sparkle chime under the big effects (Web Audio, no asset).
let _audioCtx = null;
function _ctx() {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    return _audioCtx;
  } catch (_) { return null; }
}
export function playEffectSound() {
  if (isMuted() || !isFxSoundOn()) return;
  const ctx = _ctx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    const dur = 0.6;
    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2);
    const src = ctx.createBufferSource(); src.buffer = buffer;
    const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.Q.value = 0.7;
    bp.frequency.setValueAtTime(400, now); bp.frequency.exponentialRampToValueAtTime(3000, now + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now); g.gain.exponentialRampToValueAtTime(0.5, now + 0.05); g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    src.connect(bp); bp.connect(g); g.connect(ctx.destination);
    src.start(now); src.stop(now + dur);
    const o = ctx.createOscillator(); o.type = "triangle";
    o.frequency.setValueAtTime(660, now); o.frequency.exponentialRampToValueAtTime(1320, now + 0.4);
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.0001, now); g2.gain.exponentialRampToValueAtTime(0.25, now + 0.08); g2.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
    o.connect(g2); g2.connect(ctx.destination); o.start(now); o.stop(now + 0.5);
  } catch (_) {}
}

// Effect-matched "shimmer tail" frequencies for the gift-open chime.
const _GIFT_TONE = {
  "star-rain": [1200, 2600], "star-explosion": [900, 2400], "rainbow-burst": [700, 2000],
  "confetti": [1000, 1500], "hearts": [560, 840], "bubbles": [500, 1400],
  "fireworks": [300, 1800], "snow": [1400, 2200],
};

// A cheerful gift-unwrap chime (ascending arpeggio) + a short tone that matches
// the effect raining down. Web Audio only — no asset. Respects global mute.
export function playGiftOpen(effect) {
  if (isMuted()) return;
  const ctx = _ctx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    // Unwrap: bright C-E-G-C arpeggio.
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
      const t = now + i * 0.085;
      const o = ctx.createOscillator(); o.type = "triangle";
      const g = ctx.createGain();
      o.frequency.setValueAtTime(f, t);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.3, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.26);
      o.connect(g); g.connect(ctx.destination); o.start(t); o.stop(t + 0.28);
    });
    // Effect-matched shimmer sweep tail.
    const [lo, hi] = _GIFT_TONE[effect] || [800, 1600];
    const t2 = now + 0.34;
    const o2 = ctx.createOscillator(); o2.type = "sine";
    const g2 = ctx.createGain();
    o2.frequency.setValueAtTime(lo, t2);
    o2.frequency.exponentialRampToValueAtTime(hi, t2 + 0.45);
    g2.gain.setValueAtTime(0.0001, t2);
    g2.gain.exponentialRampToValueAtTime(0.26, t2 + 0.06);
    g2.gain.exponentialRampToValueAtTime(0.0001, t2 + 0.55);
    o2.connect(g2); g2.connect(ctx.destination); o2.start(t2); o2.stop(t2 + 0.56);
  } catch (_) {}
}
